<script setup lang="ts">
import { calculateSliderOverflow } from "@/helpers/calculateSliderOverflow";
import { formatLocalDate } from "@/helpers/formatters";
import type { BlogHighlightStoryblok } from "~/types/storyblok";
import { SwiperSlide } from "swiper/vue";
import { transformUrl } from "@/helpers/link-transformers";

defineProps<{ blok: BlogHighlightStoryblok }>();

const { width } = useWindowSize();

const slideOffset = ref(24);

watch(width, (v) => (slideOffset.value = calculateSliderOverflow(v)), { immediate: true });
</script>

<template>
	<div v-editable="blok" class="bg-sand">
		<div class="py-6 md:py-10 space-y-4">
			<h3 class="container text-[2rem] md:text-5xl font-brush">
				{{ blok.title }}
			</h3>
			<div class="container hidden xl:grid grid-cols-4 gap-8">
				<template v-for="blog in blok.blogs" :key="blog.id">
					<KippieBlogCard v-if="typeof blog !== 'string'" :link="{ to: transformUrl(blog.full_slug) }">
						<template #image>
							<NuxtImg
								:src="blog.content?.thumbnail.filename"
								:alt="blog.content.thumbnail.alt"
								provider="storyblok"
								:modifiers="{ filters: { focal: blog.content.thumbnail.focus } }"
								:quality="90"
								width="700"
								height="500"
								sizes="xl:33vw lg:33vw md:33vw sm:50vw xs:100vw"
								class="w-full aspect-[63/44] object-cover"
							/>
						</template>

						<template #title>
							<h3 class="font-bold text-lg">
								{{ blog.content.ctaTitle }}
							</h3>
						</template>

						<template #date>
							{{ formatLocalDate(blog.published_at) }}
						</template>
					</KippieBlogCard>
				</template>
			</div>
			<KippieCarousel :slide-offset="slideOffset" class="xl:hidden">
				<SwiperSlide
					v-for="blog in blok.blogs"
					:key="typeof blog !== 'string' ? blog.id : blog"
					class="max-w-[18.75rem] md:max-w-[300px]"
				>
					<KippieBlogCard v-if="typeof blog !== 'string'" :link="{ to: transformUrl(blog.full_slug) }">
						<template #image>
							<NuxtImg
								:src="blog.content.thumbnail.filename"
								:alt="blog.content.thumbnail.alt"
								provider="storyblok"
								:modifiers="{ filters: { focal: blog.content.thumbnail.focus } }"
								:quality="90"
								sizes="xl:33vw lg:33vw md:33vw sm:50vw xs:100vw"
								class="w-full h-full object-cover aspect-[63/44]"
							/>
						</template>

						<template #title>
							<h3 class="font-bold text-lg">
								{{ blog.content.title }}
							</h3>
						</template>

						<template #date>
							{{ formatLocalDate(blog.published_at) }}
						</template>
					</KippieBlogCard>
				</SwiperSlide>
			</KippieCarousel>
		</div>
	</div>
</template>
